import { cn } from "@/ui/utils/cn";
import { ReactNode } from "react";

type Tone = "white" | "mist" | "navy";

const tones: Record<Tone, string> = {
  white: "bg-white text-ink",
  mist: "bg-mist text-ink",
  navy: "bg-navy text-white",
};

/**
 * Page section with the site's vertical rhythm. `tone` sets the surface;
 * everything inside uses the container width.
 */
export const Section = ({
  children,
  tone = "white",
  className,
  id,
  as: Tag = "section",
  tight = false,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
  id?: string;
  as?: "section" | "div" | "header" | "footer";
  tight?: boolean;
}) => (
  <Tag
    id={id}
    className={cn(
      tones[tone],
      tight ? "py-12 md:py-16" : "py-16 md:py-28",
      className,
    )}
  >
    <div className="container">{children}</div>
  </Tag>
);

/** Section heading with an optional lead paragraph, start-aligned. */
export const SectionHeading = ({
  title,
  lead,
  as: Tag = "h2",
  className,
  invert = false,
}: {
  title: string;
  lead?: string;
  as?: "h1" | "h2" | "h3";
  className?: string;
  invert?: boolean;
}) => (
  <div className={cn("max-w-prose", className)}>
    <Tag className="display text-h2-sm md:text-h2">{title}</Tag>
    {lead ? (
      <p
        className={cn(
          "mt-4 text-body",
          invert ? "text-slate-300" : "text-slate-600",
        )}
      >
        {lead}
      </p>
    ) : null}
  </div>
);
